import { Delete, Visibility as VisibilityIcon } from '@mui/icons-material';
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  useTheme,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '../../components/Header';
import { tokens } from '../../theme';

const CrewManagement = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('accessToken');
  const [render, setRender] = useState(false);
  const [crews, setCrews] = useState([]);

  useEffect(() => {
    const fetchCrews = async () => {
      try {
        const response = await axios.get(
          'https://webback.opencurtainscasting.com/crew/getAll',
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const crewData = response.data.users.crewProfiles.map((crew) => ({
          ...crew.user,
          ...crew.profile,
          id: crew.user.id,
        }));
        setCrews(crewData);
      } catch (error) {
        console.error('Error fetching crews:', error);
      }
    };
    fetchCrews();
  }, [token, render]);

  const DeleteUserCrew = async (userId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Are you sure want to delete this Actor`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, continue',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `https://webback.opencurtainscasting.com/crew/deleteCrew/${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setRender(!render);
        Swal.fire('Success', 'User status updated successfully', 'success');
      } catch (error) {
        Swal.fire(
          'Error',
          'There was an error updating the user status',
          'error'
        );
      }
    } else {
      Swal.fire('Canceled', 'The action has been canceled.', 'info');
    }
  };

  const handleAdminActiveChange = async (userId, newStatus) => {
    const activateStatus = newStatus ? 1 : 0;

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to ${
        newStatus ? 'activate' : 'deactivate'
      } this crew member.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, continue',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        await axios.patch(
          `https://webback.opencurtainscasting.com/user/updateUserAccess/${userId}`,
          { activate: activateStatus },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setCrews((prevCrews) =>
          prevCrews.map((crew) =>
            crew.id === userId
              ? { ...crew, adminActive: activateStatus === 1 }
              : crew
          )
        );

        Swal.fire(
          'Success',
          'Crew member status updated successfully',
          'success'
        );
      } catch (error) {
        Swal.fire(
          'Error',
          'There was an error updating the crew member status',
          'error'
        );
      }
    } else {
      Swal.fire('Canceled', 'The action has been canceled.', 'info');
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', flex: 0.3 },
    { field: 'firstName', headerName: 'First Name', flex: 1 },
    { field: 'lastName', headerName: 'Last Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1.3 },
    { field: 'contactNumber', headerName: 'Contact Number', flex: 1 },
    { field: 'gender', headerName: 'Gender', flex: 1 },
    { field: 'role', headerName: 'Role', flex: 1 },
    {
      field: 'adminActive',
      headerName: 'Admin Active',
      flex: 1,
      renderCell: ({ row }) => (
        <Select
          value={row.adminActive ? 'Active' : 'Deactivate'}
          onChange={(event) =>
            handleAdminActiveChange(row.id, event.target.value === 'Active')
          }
          displayEmpty
          variant='standard'
          sx={{
            fontWeight: 'bold',
            color: row.adminActive ? '#6e0506' : colors.redAccent[500],
          }}
        >
          <MenuItem value='Active'>Active</MenuItem>
          <MenuItem value='Deactivate'>Deactivate</MenuItem>
        </Select>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title='View'>
            <Link to={`/crew/${row.id}`}>
              <IconButton sx={{ color: '#6e0506', borderRadius: '4px' }}>
                <VisibilityIcon />
              </IconButton>
            </Link>
          </Tooltip>
          <Tooltip title='Delete' sx={{ outline: 'none' }}>
            <IconButton
              sx={{ color: '#6e0506', borderRadius: '4px' }}
              onClick={() => DeleteUserCrew(row?.id)}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box color={'#6e0506'} m='20px'>
      <Header title='CREW MEMBERS' subtitle='Manage Crew Members' />
      <Box
        m='40px 0 0 0'
        height='70vh'
        color={'#6e0506'}
        sx={{
          '& .MuiDataGrid-root': {
            border: 'none',
            fontSize: '14px',
            color: '#6e0506',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
            color: '#6e0506',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'rgba(112, 14, 16, 0.2)',
            borderTopLeftRadius: '5px',
            borderTopRightRadius: '5px',
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: 'none',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: 'rgba(112, 14, 16, 0.2)',
            borderBottomLeftRadius: '5px',
            borderBottomRightRadius: '5px',
          },
          '& .MuiCheckbox-root': {
            color: `${colors.greenAccent[200]} !important`,
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(255, 206, 207, 0.5)',
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(112, 14, 16, 0.3)',
            borderRadius: '4px',
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(112, 14, 16, 0.5)',
          },
        }}
      >
        <DataGrid
          rows={crews}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          getRowId={(row) => row.id}
          sx={{ color: '#6e0506' }}
        />
      </Box>
    </Box>
  );
};

export default CrewManagement;
